import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef, useState } from 'react';

import { WORK_PHOTOS } from '@/lib/public-content';
import { cn } from '@/lib/utils';

/*
 * The seven work photographs, with previous/next controls — public-site.md's
 * "Our Work" section.
 *
 * **No autoplay.** The live site's carousel has controls and this one keeps
 * them; adding motion nobody asked for is a rebuild changing the site, and an
 * auto-advancing carousel is also the classic way to move content out from
 * under somebody who is reading it.
 *
 * **Keyboard-reachable, which the prototype's is not.** Arrow keys move between
 * photographs when the strip has focus, and both buttons are real buttons at
 * 44px. design-system.md §9.4 asks for a keyboard path to anything driven by a
 * gesture.
 */

export function WorkCarousel() {
  const [index, setIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);

  const count = WORK_PHOTOS.length;

  function go(next: number) {
    // Wraps, because a carousel that stops at the end makes somebody who has
    // reached photo seven press "previous" six times to see photo one.
    const wrapped = (next + count) % count;
    setIndex(wrapped);
  }

  return (
    <div className="mt-8">
      <div
        ref={trackRef}
        role="group"
        aria-roledescription="carousel"
        aria-label="Our work"
        tabIndex={0}
        className="relative overflow-hidden rounded-lg border border-ink-300 shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700 focus-visible:ring-offset-2"
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            go(index + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            go(index - 1);
          }
        }}
      >
        {/*
          **Stacked and cross-faded**, rather than one shown and the rest
          `hidden`. A hard swap makes the control feel like a page reload; a
          400ms fade makes it feel like one photograph turning into the next.
          The first is in normal flow and sets the height — the rest are
          absolutely positioned over it, so the box never jumps.
        */}
        {WORK_PHOTOS.map((photo, position) => (
          <img
            key={photo.src}
            src={photo.src}
            alt={photo.alt}
            // The first is eager so the section is never empty on arrival;
            // the rest load as they are reached.
            loading={position === 0 ? 'eager' : 'lazy'}
            // Hidden from assistive technology *and* from the tab order when it
            // is not the one on screen — a faded-out image is still in the
            // accessibility tree otherwise.
            aria-hidden={position !== index}
            className={cn(
              'aspect-[16/10] w-full bg-ink-100 object-cover',
              'transition-opacity duration-500 ease-out',
              position === 0 ? 'relative' : 'absolute inset-0',
              position === index ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-4">
        <div className="flex gap-2">
          <CarouselButton
            label="Previous photo"
            onClick={() => {
              go(index - 1);
            }}
          >
            <ChevronLeft className="size-5" aria-hidden />
          </CarouselButton>
          <CarouselButton
            label="Next photo"
            onClick={() => {
              go(index + 1);
            }}
          >
            <ChevronRight className="size-5" aria-hidden />
          </CarouselButton>
        </div>

        {/*
          A live region, so somebody using a screen reader is told the photo
          changed. Without it the buttons appear to do nothing at all.
        */}
        <p aria-live="polite" className="text-meta text-ink-500">
          {index + 1} of {count}
        </p>
      </div>
    </div>
  );
}

function CarouselButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      // Never an icon-only button without an `aria-label` — design-system.md
      // §10. 44×44, like everything else that a finger has to hit.
      aria-label={label}
      onClick={onClick}
      className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-md border border-bark-700 bg-white text-bark-700 transition-colors hover:bg-honey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700 focus-visible:ring-offset-2"
    >
      {children}
    </button>
  );
}
